import { QueryClient } from '@tanstack/react-query'
import { IssueT } from '@/routes/projectStages/issues/lib/types/issuesTypes.ts'
import {
  clearAttachmentsByStage,
  clearWebAttachmentMetadataByStage,
} from '@/lib/prefetch/offlineAttachmentCache.ts'
import { collectAttachmentUrls } from '@/lib/prefetch/prefetchAttachments.ts'
import { getAssetUrl } from '@/config/assetUrl.ts'
import { isNativePlatform } from '@/lib/platform/platformDetection.ts'
import { pausePersist, resumePersist } from '@/lib/queryPersister.ts'

const ATTACHMENT_CACHES = ['media-uploads-cache', 'image-cache'] as const

/** Stage-scoped query roots and the key parameter that holds the stage id. */
const STAGE_QUERY_PARAMS: ReadonlyArray<readonly [string, string]> = [
  ['stageIssuesByIssueType', 'projectStageId'],
  ['currStageUsersQuery', 'projectStageId'],
  ['currStageIssueTypes', 'projectStageId'],
  ['stageLocations', 'id'],
]

const STAGE_METADATA_QUERY_PARAMS: ReadonlyArray<readonly [string, string]> = [
  ['projectStage', 'id'],
  ['getProjectStageLocations', 'projectStageId'],
  ['getProjectStageParticipations', 'stageId'],
  ['quickCaptureEnabled', 'projectStageId'],
  ['voiceDictationEnabled', 'projectStageId'],
]

const STAGE_PARAM_BY_ROOT = new Map<string, string>([
  ...STAGE_QUERY_PARAMS,
  ...STAGE_METADATA_QUERY_PARAMS,
])

/** True when `key` is a `root` query whose stage parameter is `stageId`, number or string. */
export function stageKeyMatches(key: readonly unknown[], root: string, stageId: string): boolean {
  if (key[0] !== root) return false
  const param = STAGE_PARAM_BY_ROOT.get(root)
  const params = key[1]
  if (!param || params == null || typeof params !== 'object') return false
  const value = (params as Record<string, unknown>)[param]
  return value != null && String(value) === stageId
}

function urlPathname(input: string): string | null {
  try {
    return new URL(input, self.location.href).pathname
  } catch {
    return null
  }
}

/**
 * Web-only: remove this stage's attachments from the Workbox runtime caches.
 * Walks each cache's keys and deletes entries whose pathname matches one of
 * the stage's attachment URLs (ignoring the rotating ?token query string).
 * Native uses the Dexie-indexed Capacitor Filesystem path instead.
 */
async function clearWebAttachmentsForStage(
  queryClient: QueryClient,
  stageId: string,
): Promise<void> {
  if (typeof caches === 'undefined') return
  try {
    const wanted = new Set<string>()
    for (const url of collectAttachmentUrls(queryClient, stageId)) {
      const path = urlPathname(getAssetUrl(url) || url)
      if (path) wanted.add(path)
    }
    if (wanted.size === 0) return

    for (const cacheName of ATTACHMENT_CACHES) {
      const cache = await caches.open(cacheName)
      const requests = await cache.keys()
      await Promise.all(
        requests
          .filter(req => {
            const path = urlPathname(req.url)
            return path != null && wanted.has(path)
          })
          .map(req => cache.delete(req).catch(() => false)),
      )
    }
  } catch {
    // Best-effort — failures shouldn't block the rest of the clear flow.
  }
}

/**
 * Remove all offline data belonging to a single project stage:
 * - cached attachment files + Dexie rows
 * - per-stage React Query entries (lists, locations, users, issue types)
 * - per-issue caches (singleIssue + comments + events + AI suggestions) for issues that
 *   were owned by this stage's lists, but only when those issues don't
 *   appear in any other stage's list (a defensive guard against accidental
 *   wipe of an issue that's referenced from multiple stages).
 *
 * Stage ids are compared as strings: some keys carry the id as a number (the stage switcher
 * reads issue types with `projectStage.id`), and TanStack matches `11` and `'11'` as different
 * keys. `stageMetadata` also drops the stage's own record and settings lists — wanted when the
 * stage is gone for good (archived or deleted, D7), not when the user only clears its download.
 *
 * Persistence is paused during the bulk removeQueries calls so
 * `react-query-persist-client` doesn't dehydrate the entire cache after
 * every individual removal — same OOM-avoidance pattern the prefetch uses.
 */
export async function clearStageCache(
  queryClient: QueryClient,
  stageId: string,
  options: { stageMetadata?: boolean } = {},
): Promise<void> {
  // 1. Collect issue IDs that belong to this stage
  const stageLists = queryClient.getQueriesData<IssueT[]>({
    predicate: query => stageKeyMatches(query.queryKey, 'stageIssuesByIssueType', stageId),
  })

  const ownedIssueIds = new Set<string>()
  for (const [, issues] of stageLists) {
    if (!issues) continue
    for (const issue of issues) {
      ownedIssueIds.add(String(issue.id))
    }
  }

  // 2. Determine which of those IDs also appear in *other* stages' lists.
  //    Those should NOT be removed from singleIssue caches.
  const sharedIssueIds = new Set<string>()
  if (ownedIssueIds.size > 0) {
    const allLists = queryClient.getQueriesData<IssueT[]>({
      queryKey: ['stageIssuesByIssueType'],
    })
    for (const [key, issues] of allLists) {
      if (stageKeyMatches(key, 'stageIssuesByIssueType', stageId) || !issues) continue
      for (const issue of issues) {
        const id = String(issue.id)
        if (ownedIssueIds.has(id)) sharedIssueIds.add(id)
      }
    }
  }

  // 3. Drop attachments first — independent of React Query state.
  //    Native uses Dexie + Capacitor Filesystem; web uses the SW cache.
  //    Important: web's `cache.delete` needs the URL list still resolvable
  //    from React Query, so call this BEFORE we remove the issue caches.
  if (isNativePlatform()) {
    await clearAttachmentsByStage(stageId).catch(() => {})
  } else {
    // Drop SW-cache entries first (uses RQ data to derive URLs), then drop
    // the Dexie metadata rows so size measurement immediately reflects 0.
    await clearWebAttachmentsForStage(queryClient, stageId)
    await clearWebAttachmentMetadataByStage(stageId)
  }

  // 4. Remove per-stage React Query entries, with persistence paused.
  pausePersist()
  try {
    const roots = options.stageMetadata
      ? [...STAGE_QUERY_PARAMS, ...STAGE_METADATA_QUERY_PARAMS]
      : STAGE_QUERY_PARAMS
    queryClient.removeQueries({
      predicate: query => roots.some(([root]) => stageKeyMatches(query.queryKey, root, stageId)),
    })

    // 5. Drop per-issue caches that aren't shared with another stage.
    for (const id of ownedIssueIds) {
      if (sharedIssueIds.has(id)) continue
      queryClient.removeQueries({ queryKey: ['singleIssue', { id }] })
      queryClient.removeQueries({ queryKey: ['singleIssueComments', { id }] })
      queryClient.removeQueries({ queryKey: ['singleIssueEvents', { id }] })
      queryClient.removeQueries({ queryKey: ['issueAiSuggestions', { issueId: id }] })
    }
  } finally {
    await resumePersist()
  }
}
